/**
 * "Leave Dates" range filter for HR Applied Leaves.
 *
 * Same picker as /apply and the Out of System form (`RangeCalendar`), but as a
 * floating popover so it doesn't push the table down. The popover aligns to
 * whichever side of the trigger keeps it on screen; Escape or a click outside
 * closes it. Filters by leave START date (`since`/`until`).
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'

import { listPublicHolidays } from '../../api/public-holidays'
import { RangeCalendar } from '../employee/RangeCalendar'
import { Button } from '../primitives/Button'
import { FadeIn } from '../primitives/FadeIn'
import { Icon } from '../primitives/Icon'
import { useViewport } from '../../lib/use-viewport'

export interface LeaveDatesFilterProps {
  since: string | undefined
  until: string | undefined
  onChange: (since: string | null, until: string | null) => void
  /** Selectable bounds (ISO dates, inclusive). */
  min?: string | undefined
  max?: string | undefined
  /** Month the calendar opens on when nothing is picked. */
  initialView?: string | undefined
  /** Labelled-by id from the field label. */
  labelId?: string
  /** Host filter bar's control style (height, padding, background, font) so
   *  the trigger lines up with the neighbouring inputs. */
  controlStyle?: CSSProperties
}

/** Two 40px-cell months side by side + gap + padding (desktop). The popover
 *  needs an explicit width: absolutely positioned inside the narrow field it
 *  would otherwise shrink to the trigger's width and crush the grid. */
const POPOVER_WIDTH_DESKTOP = 660
const POPOVER_WIDTH_MOBILE = 340
const VIEWPORT_GUTTER = 16

function rangeLabel(since: string | undefined, until: string | undefined): string {
  if (!since) return ''
  const start = format(parseISO(since), 'dd MMM yyyy')
  if (!until || until === since) return start
  return `${start} - ${format(parseISO(until), 'dd MMM yyyy')}`
}

export function LeaveDatesFilter({
  since,
  until,
  onChange,
  min,
  max,
  initialView,
  labelId,
  controlStyle,
}: LeaveDatesFilterProps): JSX.Element {
  const { isDesktop } = useViewport()
  const [open, setOpen] = useState(false)
  const [alignRight, setAlignRight] = useState(false)
  // Reopening onto an existing selection → the next click starts a fresh pick
  // instead of extending the committed range (mirrors /apply).
  const [freshPick, setFreshPick] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const holidaysQuery = useQuery({
    queryKey: ['public-holidays'],
    queryFn: listPublicHolidays,
    staleTime: 60_000,
    enabled: open,
  })
  const publicHolidays = useMemo(
    () => (holidaysQuery.data ?? []).map((h) => h.date),
    [holidaysQuery.data],
  )
  const holidayNames = useMemo(
    () => (holidaysQuery.data ?? []).map((h) => ({ date: h.date, name: h.name })),
    [holidaysQuery.data],
  )

  useEffect(() => {
    if (!open) return
    const onPointer = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function toggle(): void {
    if (!open) {
      const rect = containerRef.current?.getBoundingClientRect()
      const width = isDesktop ? POPOVER_WIDTH_DESKTOP : POPOVER_WIDTH_MOBILE
      setAlignRight(
        rect !== undefined && rect.left + width > window.innerWidth - VIEWPORT_GUTTER,
      )
      if (since) setFreshPick(true)
    }
    setOpen((o) => !o)
  }

  function handleRange(from: string, to: string): void {
    if (freshPick) {
      setFreshPick(false)
      const clicked = to !== '' ? to : from
      onChange(clicked, clicked)
      return
    }
    onChange(from || null, to || null)
    // First click sets a 1-day range and stays open; a later date or a
    // re-click on the same selected day completes the pick and closes.
    const reclickSameDay = from !== '' && from === to && since === from && until === to
    if ((from && to && to > from) || reclickSameDay) setOpen(false)
  }

  const label = rangeLabel(since, until)

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        data-testid="leave-dates-trigger"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        {...(labelId ? { 'aria-labelledby': labelId } : {})}
        style={triggerStyle(open, controlStyle)}
      >
        <Icon name="calendar_month" size={16} color="var(--text-3)" aria-hidden="true" />
        {label ? (
          <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</span>
        ) : (
          <span style={{ color: 'var(--text-3)' }}>Any dates</span>
        )}
        <span style={caretStyle(open)}>
          <Icon name="expand_more" size={16} aria-hidden="true" />
        </span>
      </button>

      {open ? (
        <div style={{ ...popoverPositionStyle, ...(alignRight ? { right: 0 } : { left: 0 }) }}>
          <FadeIn rise={4} duration="fast">
            <div
              role="dialog"
              aria-label="Pick leave dates"
              style={{
                ...popoverStyle,
                width: isDesktop
                  ? POPOVER_WIDTH_DESKTOP
                  : `min(${POPOVER_WIDTH_MOBILE}px, calc(100vw - ${VIEWPORT_GUTTER * 2}px))`,
              }}
            >
              <RangeCalendar
                from={since ?? ''}
                to={until ?? ''}
                onChange={handleRange}
                publicHolidays={publicHolidays}
                holidayNames={holidayNames}
                min={min}
                max={max}
                initialView={initialView}
                oneClickSingleDay
                hideBlackoutLegend
                mobile={!isDesktop}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <Button
                  type="button"
                  variant="tertiary"
                  size="sm"
                  data-testid="leave-dates-clear"
                  onClick={() => onChange(null, null)}
                >
                  Clear
                </Button>
              </div>
            </div>
          </FadeIn>
        </div>
      ) : null}
    </div>
  )
}

function triggerStyle(open: boolean, controlStyle: CSSProperties | undefined): CSSProperties {
  return {
    ...baseTriggerStyle,
    ...controlStyle,
    // Layout + open-state ring always win over the host style.
    width: '100%',
    minWidth: 220,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    textAlign: 'left',
    border: `1px solid ${open ? 'var(--steel-blue)' : 'var(--border)'}`,
    boxShadow: open ? '0 0 0 3px var(--baby-blue-tint)' : 'none',
    transition: 'border-color .12s, box-shadow .12s',
  }
}

/** Standalone defaults (44px hit target); a host `controlStyle` overrides. */
const baseTriggerStyle: CSSProperties = {
  padding: '0 12px',
  minHeight: 44,
  background: 'var(--surface)',
  borderRadius: 8,
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  fontWeight: 400,
  color: 'var(--text)',
  textTransform: 'none',
  letterSpacing: 'normal',
}

function caretStyle(open: boolean): CSSProperties {
  return {
    marginLeft: 'auto',
    color: 'var(--text-3)',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'transform .15s',
    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
  }
}

const popoverPositionStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  zIndex: 30,
}

// The field sits inside an uppercase mono filter label; reset the type so the
// calendar renders exactly as on /apply.
const popoverStyle: CSSProperties = {
  boxSizing: 'border-box',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
  fontWeight: 400,
  textTransform: 'none',
  letterSpacing: 'normal',
  color: 'var(--text)',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: 12,
  boxShadow: 'var(--shadow-lg, 0 12px 32px rgba(0,0,0,.12))',
  maxWidth: `calc(100vw - ${VIEWPORT_GUTTER * 2}px)`,
}
